import apiClient from './apiClient';
import toast from 'react-hot-toast';
import { getUserFriendlyError } from '../utils/errorHandlers';

/**
 * User Management Service
 * 
 * Handles CRUD operations for user management across all roles.
 * CRITICAL: All functions require tenantId to enforce multi-tenant data isolation.
 * 
 * @module userManagementService
 */

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate that tenantId is provided
 * @param {string} tenantId - Tenant ID
 * @throws {Error} If tenantId is missing
 */
const validateTenantId = (tenantId) => {
  if (!tenantId || typeof tenantId !== 'string') {
    throw new Error('tenantId is required and must be a valid string');
  }
};

/**
 * Validate pagination parameters
 * @param {number} page - Page number (0-indexed)
 * @param {number} pageSize - Number of items per page
 */
const validatePagination = (page, pageSize) => {
  if (typeof page !== 'number' || page < 0) {
    throw new Error('page must be a non-negative number');
  }
  if (typeof pageSize !== 'number' || pageSize < 1 || pageSize > 100) {
    throw new Error('pageSize must be between 1 and 100');
  }
};

// ============================================================================
// USER LISTING & SEARCH
// ============================================================================

/**
 * List users with pagination, filtering, and sorting
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.tenantId - Required tenant ID
 * @param {string} [params.role] - Filter by role (patient, physician, clinic_staff, etc.)
 * @param {string} [params.q] - Search query (searches name, email)
 * @param {string} [params.status] - Filter by status (active, inactive, pending)
 * @param {number} [params.page=1] - Page number (1-indexed, backend requires >= 1)
 * @param {number} [params.pageSize=10] - Items per page
 * @param {string} [params.sortBy='created_at'] - Sort column
 * @param {string} [params.sortOrder='desc'] - Sort direction (asc, desc)
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const listUsers = async ({
  tenantId,
  role,
  q,
  status,
  page = 1,
  pageSize = 10,
  sortBy = 'created_at',
  sortOrder = 'desc',
  userIds = [],
}) => {
  try {
    // Validate required parameters
    validateTenantId(tenantId);
    // Backend expects page >= 1, so adjust validation
    if (typeof page !== 'number' || page < 1) {
      throw new Error('page must be a positive number (>= 1)');
    }
    validatePagination(page - 1, pageSize); // Validate as 0-indexed for internal checks

    // Build query params for API
    const params = {
      page,
      pageSize,
      sortBy,
      sortOrder,
    };

    if (role && role !== 'all') {
      params.role = role;
    }

    if (status && status !== 'all') {
      params.status = status;
    }

    if (q && q.trim()) {
      params.q = q.trim();
    }

    if (userIds && userIds.length > 0) {
      params.userIds = userIds.join(',');
    }

    // Call API endpoint
    const response = await apiClient.get('/users', params);

    return {
      data: {
        rows: response.rows || [],
        total: response.total || 0,
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
      },
      error: null,
    };
  } catch (error) {
    console.error('Error listing users:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Get a single user by ID
 * 
 * @param {string} userId - User ID
 * @param {string} tenantId - Required tenant ID
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const getUser = async (userId, tenantId) => {
  try {
    validateTenantId(tenantId);

    if (!userId) {
      throw new Error('userId is required');
    }

    const data = await apiClient.getUserById(userId);
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching user:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

// ============================================================================
// USER CREATION
// ============================================================================

/**
 * Generate a random password
 * @param {number} length - Password length (default: 12)
 * @returns {string} Generated password
 */
const generatePassword = (length = 12) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

/**
 * Create a new user
 * 
 * @param {Object} userData - User data
 * @param {string} userData.tenantId - Required tenant ID
 * @param {string} userData.email - User email
 * @param {string} userData.first_name - First name
 * @param {string} userData.last_name - Last name
 * @param {string} userData.tenant_role - User role within tenant
 * @param {string} [userData.phone] - Phone number
 * @param {string} [userData.password] - User password (auto-generated if not provided)
 * @param {string} [userData.status='active'] - User status (active for admin-created users)
 * @param {Object} [userData.additionalData] - Role-specific additional data
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const createUser = async (userData) => {
  try {
    const { 
      tenantId, 
      email, 
      first_name, 
      last_name, 
      tenant_role, 
      password,
      status = 'active',
      ...rest 
    } = userData;

    validateTenantId(tenantId);

    if (!email || !first_name || !last_name || !tenant_role) {
      throw new Error('email, first_name, last_name, and tenant_role are required');
    }

    // Generate password if not provided
    const userPassword = password || generatePassword();

    // Call backend API to create user (creates auth user + profile)
    const userPayload = {
      email,
      firstName: first_name,
      lastName: last_name,
      tenantRole: tenant_role,
      tenantId,
      password: userPassword,
      status,
      ...rest,
    };

    const response = await apiClient.post('/users', userPayload);

    // Log response for debugging
    if (import.meta.env.DEV) console.log('[createUser] API response:', response);

    // Handle different response structures
    // Backend returns: { success: true, message: '...', data: { user, profile, password } }
    // apiClient.handleResponse extracts data.data, so response should be { user, profile, password }
    // But handle edge cases where response structure might differ
    let responseData = response;
    if (response && typeof response === 'object') {
      // If response has nested data structure, extract it
      if (response.data && !response.user && !response.profile) {
        responseData = response.data;
      }
      // If response is the full API response with success/message, extract data
      if (response.success && response.data) {
        responseData = response.data;
      }
    }

    // Validate that we got user data back
    if (!responseData || (!responseData.user && !responseData.profile)) {
      console.warn('[createUser] Unexpected response structure:', response);
      console.warn('[createUser] Extracted responseData:', responseData);
      // Even if structure is unexpected, if we got a response, user might have been created
      // Return what we have rather than throwing an error
      responseData = responseData || response || {};
    }

    // Show success toast - user was created successfully
    toast.success(
      `User created successfully! Password: ${userPassword}\n\n⚠️ Save this password!`,
      { duration: 8000 }
    );
    
    // Return user data with generated password so UI can display it
    return { 
      data: { ...responseData, temporaryPassword: userPassword }, 
      error: null 
    };
  } catch (error) {
    console.error('Error creating user:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Export generatePassword for use in components
 */
export { generatePassword };

// ============================================================================
// USER UPDATE
// ============================================================================

/**
 * Update an existing user
 * 
 * @param {string} userId - User ID
 * @param {string} tenantId - Required tenant ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const updateUser = async (userId, tenantId, updates) => {
  try {
    validateTenantId(tenantId);

    if (!userId) {
      throw new Error('userId is required');
    }

    // Ensure tenant_id cannot be changed
    const sanitizedUpdates = { ...updates };
    delete sanitizedUpdates.tenant_id;
    delete sanitizedUpdates.id;
    delete sanitizedUpdates.created_at;

    // Call API endpoint
    const data = await apiClient.updateUser(userId, sanitizedUpdates);

    toast.success('User updated successfully');
    return { data, error: null };
  } catch (error) {
    console.error('Error updating user:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

// ============================================================================
// USER DELETION
// ============================================================================

/**
 * Delete a user (soft delete by setting status to 'inactive')
 * 
 * @param {string} userId - User ID
 * @param {string} tenantId - Required tenant ID
 * @param {boolean} [hardDelete=false] - If true, permanently delete; otherwise soft delete
 * @returns {Promise<{success: boolean, error: null}|{success: false, error: Error}>}
 */
export const deleteUser = async (userId, tenantId, hardDelete = false) => {
  try {
    validateTenantId(tenantId);

    if (!userId) {
      throw new Error('userId is required');
    }

    if (hardDelete) {
      // Permanent deletion via API
      await apiClient.deleteUser(userId);
      toast.success('User permanently deleted');
    } else {
      // Soft delete - deactivate via API
      await apiClient.deactivateUser(userId);
      toast.success('User deactivated successfully');
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting user:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { success: false, error };
  }
};

// ============================================================================
// BULK OPERATIONS
// ============================================================================

/**
 * Bulk invite users (placeholder for future implementation)
 * 
 * @param {string} tenantId - Required tenant ID
 * @param {Array<Object>} users - Array of user data objects
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const bulkInvite = async (tenantId, users) => {
  try {
    validateTenantId(tenantId);

    if (!Array.isArray(users) || users.length === 0) {
      throw new Error('users must be a non-empty array');
    }

    // TODO: Implement backend API for bulk user invitation
    // This should:
    // 1. Create Supabase Auth users
    // 2. Send invitation emails
    // 3. Create user profile records
    console.warn('bulkInvite: Backend API integration required');

    toast.info('Bulk invite feature coming soon');
    return {
      data: {
        invited: 0,
        failed: 0,
        results: [],
      },
      error: null,
    };
  } catch (error) {
    console.error('Error with bulk invite:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Update user status
 * 
 * @param {string} userId - User ID
 * @param {string} tenantId - Required tenant ID
 * @param {string} status - New status (active, inactive, pending)
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const updateUserStatus = async (userId, tenantId, status) => {
  try {
    validateTenantId(tenantId);

    if (!userId) {
      throw new Error('userId is required');
    }

    const validStatuses = ['active', 'inactive', 'pending'];
    if (!validStatuses.includes(status)) {
      throw new Error(`status must be one of: ${validStatuses.join(', ')}`);
    }

    // Use API endpoints for activate/deactivate
    if (status === 'active') {
      const data = await apiClient.activateUser(userId);
      toast.success('User activated successfully');
      return { data, error: null };
    } else if (status === 'inactive') {
      const data = await apiClient.deactivateUser(userId);
      toast.success('User deactivated successfully');
      return { data, error: null };
    } else {
      // For other statuses, use update endpoint
      const data = await apiClient.updateUser(userId, { status });
      toast.success(`User status updated to ${status}`);
      return { data, error: null };
    }
  } catch (error) {
    console.error('Error updating user status:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Resend credentials email
 * 
 * @param {string} userId - User ID
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const resendCredentials = async (userId) => {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }

    const data = await apiClient.resendUserCredentials(userId);

    toast.success('Credentials email sent successfully!');
    return { data, error: null };
  } catch (error) {
    console.error('Error resending credentials:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

export default {
  listUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  bulkInvite,
  updateUserStatus,
  resendCredentials,
};
